import React, { useState, useLayoutEffect, useRef } from "react";
import {
	Box,
	TextField,
	Typography,
	Snackbar,
	Alert,
	Modal,
	Fab,
	InputAdornment,
	IconButton,
	Button,
	List,
	ListItemButton,
	ListItemText,
	MenuItem
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Link as RouterLink } from "react-router-dom";
import api from "../utils/api";
import Navbar from "../components/NavBar";

const IngredientsList: React.FC = () => {
	const [ingredientName, setIngredientName] = useState("");
	const [cost, setCost] = useState<string>("");
	const [purchaseQty, setPurchaseQty] = useState<string>("");
	const [purchaseUnit, setPurchaseUnit] = useState<string>("grams");
	const [ingredients, setIngredients] = useState<{ Id: string; name: string; cost?: number; costMeasure?: string }[]>([]);
	const [searchQuery, setSearchQuery] = useState<string>("");
	const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
		open: false,
		message: "",
		severity: "success"
	});
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isEditMode, setIsEditMode] = useState(false);
	const [editingIngredientId, setEditingIngredientId] = useState<string | null>(null);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [selectedIngredient, setSelectedIngredient] = useState<{ Id: string; name: string } | null>(null);
	const [deleteModalMessage, setDeleteModalMessage] = useState("");
	const [meals, setMeals] = useState<{ name: string }[]>([]);
	const [navbarHeight, setNavbarHeight] = useState(0);

	const navbarRef = useRef<HTMLDivElement | null>(null);

	// Fetch all ingredients on component mount
	useLayoutEffect(() => {
		const fetchIngredients = async () => {
			try {
				const response = await api.get("/ingredients");
				const sortedIngredients = response.data.sort((a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name));
				setIngredients(sortedIngredients);
			} catch (error) {
				console.error("Failed to fetch ingredients", error);
			}
		};
		fetchIngredients();
	}, []);

	// Calculate the height of the navbar and set it
	useLayoutEffect(() => {
		if (navbarRef.current) {
			setNavbarHeight(navbarRef.current.offsetHeight);
		}
	}, []);

	// Calculate the per-unit cost (per kg or per litre) from purchase details
	const calculatePerUnitCost = (): { cost: number; measure: string } | null => {
		const costVal = parseFloat(cost);
		const qty = parseFloat(purchaseQty);

		if (isNaN(costVal) || isNaN(qty) || qty <= 0 || costVal < 0) {
			return null;
		}

		// Convert to base unit (kg or litre)
		if (purchaseUnit === "grams") {
			return { cost: costVal / (qty / 1000), measure: "kg" };
		} else if (purchaseUnit === "kg") {
			return { cost: costVal / qty, measure: "kg" };
		} else if (purchaseUnit === "ml") {
			return { cost: costVal / (qty / 1000), measure: "litre" };
		} else if (purchaseUnit === "litres") {
			return { cost: costVal / qty, measure: "litre" };
		} else if (purchaseUnit === "each") {
			return { cost: costVal / qty, measure: "each" };
		} else if (purchaseUnit === "dozen") {
			return { cost: costVal / (qty * 12), measure: "each" };
		}

		return null;
	};

	// Get the calculated cost for display
	const calculatedCost = calculatePerUnitCost();

	// Handle form submission for adding or editing an ingredient
	const handleSubmit = async () => {
		const formattedIngredientName = ingredientName.trim();
		if (!formattedIngredientName) {
			setSnackbar({
				open: true,
				message: "Please enter an ingredient name",
				severity: "error"
			});
			return;
		}

		// Check if ingredient already exists (only for add mode, not edit mode)
		if (!isEditMode && ingredients.some((ingredient) => ingredient.name === formattedIngredientName)) {
			setSnackbar({
				open: true,
				message: "Ingredient already exists",
				severity: "error"
			});
			return;
		}

		// Build ingredient object with optional cost fields
		const ingredientData: any = { name: formattedIngredientName };

		// If cost fields are filled, calculate and save the per-unit cost
		if (cost && purchaseQty) {
			const calculated = calculatePerUnitCost();
			if (!calculated) {
				setSnackbar({
					open: true,
					message: "Please enter valid cost and quantity",
					severity: "error"
				});
				return;
			}
			ingredientData.cost = calculated.cost;
			ingredientData.costMeasure = calculated.measure;
		}

		try {
			if (isEditMode && editingIngredientId) {
				// Update existing ingredient
				await api.put(`/ingredients/${editingIngredientId}`, ingredientData);
				setSnackbar({ open: true, message: "Ingredient updated successfully", severity: "success" });
			} else {
				// Add new ingredient
				await api.post("/ingredients", ingredientData);
				setSnackbar({ open: true, message: "Ingredient added successfully", severity: "success" });
			}

			setIngredientName("");
			setCost("");
			setPurchaseQty("");
			setPurchaseUnit("grams");

			// Refetch ingredients
			const response = await api.get("/ingredients");
			const sortedIngredients = response.data.sort((a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name));
			setIngredients(sortedIngredients);
		} catch (error) {
			console.error("Failed to save ingredient", error);
			setSnackbar({ open: true, message: "Failed to save ingredient", severity: "error" });
		}
		closeModal();
	};

	// Handle ingredient delete action
	const handleDeleteIngredient = async (ingredient: { Id: string; name: string }) => {
		try {
			let fetchedMeals = [];

			// Check if the ingredient is part of any meal
			try {
				const response = await api.get(`/meals/ingredient/${ingredient.Id}`);
				fetchedMeals = response.data;
			} catch (error) {
				if (error instanceof Error && "response" in error) {
					const axiosError = error as any;
					if (axiosError.response && axiosError.response.status === 404) {
						console.log("No meals found with the given ingredient.");
					} else {
						throw error;
					}
				} else {
					throw error;
				}
			}

			// Set the selected ingredient and show the confirmation modal with a message
			setSelectedIngredient(ingredient);
			setMeals(fetchedMeals);

			if (fetchedMeals.length > 0) {
				setDeleteModalMessage(
					"This ingredient is part of the following meals. Deleting it will remove it from all associated meals. Do you want to proceed?"
				);
			} else {
				setDeleteModalMessage("Are you sure you want to delete this ingredient?");
			}
			setIsDeleteModalOpen(true);
		} catch (error) {
			console.error("Failed to check ingredient associations", error);
			setSnackbar({ open: true, message: "Failed to check ingredient associations", severity: "error" });
		}
	};

	// Confirm deletion of ingredient and remove from meals
	const confirmDeleteIngredient = async () => {
		if (!selectedIngredient) return;

		try {
			// Delete the ingredient from meals
			await api.delete(`/mealIngredients/ingredients/${selectedIngredient.Id}`);
			// Delete the ingredient
			await api.delete(`/ingredients/${selectedIngredient.Id}`);

			setSnackbar({
				open: true,
				message: "Ingredient and related meal ingredients if required were deleted successfully",
				severity: "success"
			});

			// Update ingredient list
			const updatedIngredients = ingredients.filter((i) => i.Id !== selectedIngredient.Id);
			setIngredients(updatedIngredients);
		} catch (error) {
			console.error("Failed to delete ingredient and related meal ingredients", error);
			setSnackbar({ open: true, message: "Failed to delete ingredient", severity: "error" });
		} finally {
			setIsDeleteModalOpen(false);
			setSelectedIngredient(null);
		}
	};

	const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

	const openModal = () => {
		setIsEditMode(false);
		setEditingIngredientId(null);
		setIsModalOpen(true);
	};

	const handleEditIngredient = (ingredient: { Id: string; name: string; cost?: number; costMeasure?: string }) => {
		setIsEditMode(true);
		setEditingIngredientId(ingredient.Id);
		setIngredientName(ingredient.name);
		// Reset cost fields - user will enter new purchase details
		setCost("");
		setPurchaseQty("");
		setPurchaseUnit("grams");
		setIsModalOpen(true);
	};

	const closeModal = () => {
		setIsModalOpen(false);
		setIsEditMode(false);
		setEditingIngredientId(null);
		setIngredientName("");
		setCost("");
		setPurchaseQty("");
		setPurchaseUnit("grams");
	};

	const filteredIngredients = ingredients.filter((ingredient) => ingredient.name.toLowerCase().includes(searchQuery.toLowerCase()));

	return (
		<>
			<Navbar ref={navbarRef} />
			<Box sx={{ p: 3 }}>
				{/* Search Box with Sticky Position */}
				<Box sx={{ position: "sticky", top: `${navbarHeight}px`, backgroundColor: "white", zIndex: 1, paddingBottom: 2 }}>
					<Typography variant="h4" gutterBottom>
						Manage Ingredients
					</Typography>

					<TextField
						label="Search Ingredients"
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						slotProps={{
							input: {
								endAdornment: searchQuery && (
									<InputAdornment position="end">
										<IconButton onClick={() => setSearchQuery("")}>
											<ClearIcon />
										</IconButton>
									</InputAdornment>
								)
							}
						}}
						fullWidth
					/>
				</Box>

				{/* Ingredient List */}
				<List>
					{filteredIngredients.length > 0 ? (
						filteredIngredients.map((ingredient) => (
							<Box key={ingredient.Id} display="flex" alignItems="center">
								<ListItemButton component={RouterLink} to={`/ingredients/${ingredient.Id}`} style={{ flex: 1 }}>
									<ListItemText primary={ingredient.name} />
								</ListItemButton>
								<IconButton
									edge="end"
									aria-label="edit"
									onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
										e.stopPropagation();
										e.preventDefault();
										handleEditIngredient(ingredient);
									}}>
									<EditIcon />
								</IconButton>
								<IconButton
									edge="end"
									aria-label="delete"
									onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
										e.stopPropagation();
										e.preventDefault();
										handleDeleteIngredient(ingredient);
									}}>
									<DeleteIcon />
								</IconButton>
							</Box>
						))
					) : (
						<Typography>No ingredients found</Typography>
					)}
				</List>
			</Box>

			{/* Add Ingredient Floating Action Button */}
			<Fab color="primary" aria-label="add" onClick={openModal} sx={{ position: "fixed", bottom: 16, right: 16 }}>
				<AddIcon />
			</Fab>

			{/* Add/Edit Ingredient Modal */}
			<Modal
				open={isModalOpen}
				onClose={closeModal}
				aria-labelledby="ingredient-modal-title"
				sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
				<Box sx={{ width: "80vw", maxWidth: 400, bgcolor: "background.paper", p: 4, borderRadius: 2, boxShadow: 24 }}>
					<Typography id="ingredient-modal-title" variant="h6" gutterBottom>
						{isEditMode ? "Edit Ingredient" : "Add New Ingredient"}
					</Typography>
					<TextField
						fullWidth
						label="Enter Ingredient Name"
						variant="outlined"
						value={ingredientName}
						onChange={(e) => setIngredientName(e.target.value)}
						sx={{ marginBottom: 2 }}
						required
					/>
					<Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
						Cost Information (optional)
					</Typography>
					<Box sx={{ display: "flex", gap: 1, mb: 2 }}>
						<TextField
							label="Price Paid"
							variant="outlined"
							type="number"
							value={cost}
							onChange={(e) => setCost(e.target.value)}
							sx={{ flex: 1 }}
							slotProps={{
								input: {
									startAdornment: <InputAdornment position="start">$</InputAdornment>
								}
							}}
						/>
						<Typography variant="body1" sx={{ alignSelf: "center" }}>
							for
						</Typography>
						<TextField
							label="Qty"
							variant="outlined"
							type="number"
							value={purchaseQty}
							onChange={(e) => setPurchaseQty(e.target.value)}
							sx={{ width: 100 }}
						/>
						<TextField
							select
							label="Unit"
							variant="outlined"
							value={purchaseUnit}
							onChange={(e) => setPurchaseUnit(e.target.value)}
							sx={{ width: 100 }}>
							<MenuItem value="grams">g</MenuItem>
							<MenuItem value="kg">kg</MenuItem>
							<MenuItem value="ml">ml</MenuItem>
							<MenuItem value="litres">L</MenuItem>
							<MenuItem value="each">each</MenuItem>
							<MenuItem value="dozen">dozen</MenuItem>
						</TextField>
					</Box>
					{calculatedCost && (
						<Alert severity="info" sx={{ mb: 2 }}>
							${calculatedCost.cost.toFixed(2)} per {calculatedCost.measure}
						</Alert>
					)}
					<Button variant="contained" color="primary" onClick={handleSubmit} fullWidth>
						Save
					</Button>
				</Box>
			</Modal>

			{/* Delete Confirmation Modal */}
			<Modal
				open={isDeleteModalOpen}
				onClose={() => setIsDeleteModalOpen(false)}
				aria-labelledby="delete-confirmation-modal-title"
				sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
				<Box sx={{ width: "80vw", maxWidth: 400, bgcolor: "background.paper", p: 4, borderRadius: 2, boxShadow: 24 }}>
					<Typography id="delete-confirmation-modal-title" variant="h6" gutterBottom>
						Delete Ingredient
					</Typography>
					{meals.length > 0 ? (
						<>
							<Typography gutterBottom>{deleteModalMessage}</Typography>
							<Box component="ul" sx={{ mt: 2, pl: 3 }}>
								{meals.map((meal) => (
									<li key={meal.name}>
										<Typography variant="body2">{meal.name}</Typography>
									</li>
								))}
							</Box>
						</>
					) : (
						<Typography>{deleteModalMessage}</Typography>
					)}
					<Box mt={3} display="flex" justifyContent="space-between">
						<Button variant="outlined" onClick={() => setIsDeleteModalOpen(false)}>
							Cancel
						</Button>
						<Button variant="contained" color="error" onClick={confirmDeleteIngredient}>
							Delete
						</Button>
					</Box>
				</Box>
			</Modal>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={6000}
				onClose={handleCloseSnackbar}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
				<Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</>
	);
};

export default IngredientsList;
